import React from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

/**
 * MessageButton — appears on author bylines (Weekly cards, CorrelationModal,
 * PublicProfile). Tapping routes the current user to `/messages/<slug>`.
 *
 * Props:
 *   slug        — recipient handle (preferred) or user_id
 *   ownerUserId — recipient's user_id; used to hide the button on self-views
 *   variant     — "icon" (default, compact) | "pill" (labelled CTA)
 *   testid      — optional override
 */
export default function MessageButton({ slug, ownerUserId, variant = "icon", testid }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!slug) return null;
  // Don't show the button on your own author bylines.
  if (user && ownerUserId && user.user_id === ownerUserId) return null;

  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Sign in to send messages.");
      navigate("/login");
      return;
    }
    navigate(`/messages/${slug}`);
  };

  if (variant === "pill") {
    return (
      <button
        type="button"
        data-testid={testid || `message-pill-${slug}`}
        onClick={onClick}
        className="inline-flex items-center gap-2 mono text-[11px] uppercase tracking-[0.22em] px-4 py-2 border border-border text-muted-foreground hover:text-[hsl(var(--primary))] hover:border-[hsl(var(--primary))] transition-colors"
      >
        <MessageSquare size={13} strokeWidth={2} />
        Message
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid={testid || `message-btn-${slug}`}
      onClick={onClick}
      title="Send a private message"
      aria-label={`Message @${slug}`}
      className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 hover:text-[hsl(var(--primary))] transition-colors"
    >
      <MessageSquare size={12} strokeWidth={2} />
      <span>Message</span>
    </button>
  );
}
